<template>
  <!-- Los procesos anteriores al 19 de octubre de 2026 no tienen trazabilidad.
       El endpoint devuelve 404 y el bloque sencillamente no se dibuja: no hace
       falta lógica de fechas ni un mensaje de "no disponible". -->
  <vx-card v-if="hayDatos" title="Tiempos y línea de tiempo">
    <p class="gw-bajada">
      Estado, plazo y tiempo consumido de cada validación, con los eventos
      registrados y el responsable de cada tramo. Los tiempos se expresan en
      horas y días hábiles, la unidad del acuerdo de niveles de servicio.
    </p>

    <div
      v-for="(v, i) in validaciones"
      :key="i"
      class="gw-validacion"
    >
      <!-- Cabecera de la validación: estado, vence, consumido -->
      <div class="gw-v-cabecera">
        <div class="gw-v-identidad">
          <span class="gw-v-nombre">{{ etiquetaValidacion(v.validacion) }}</span>
          <span class="gw-v-estado">{{ v.estado }}</span>
          <span v-if="v.datos_aproximados" class="gw-v-aprox" :title="tituloAproximado">
            datos reconstruidos
          </span>
        </div>

        <div class="gw-v-ans">
          <template v-if="v.ans_resolucion && v.ans_resolucion.aplica">
            <span :class="['gw-chip', 'gw-chip--' + tonoUrgencia(v.ans_resolucion.estado)]">
              {{ etiquetaUrgencia(v.ans_resolucion.estado) }}
            </span>
            <span class="gw-v-vence" v-if="v.ans_resolucion.vence">
              vence {{ fechaCorta(v.ans_resolucion.vence) }}
            </span>
          </template>
          <span v-else class="gw-chip gw-chip--neutro">Sin plazo pactado</span>
        </div>
      </div>

      <!-- Consumido contra presupuesto -->
      <div v-if="v.ans_resolucion && v.ans_resolucion.aplica" class="gw-consumo">
        <div class="gw-barra">
          <div
            class="gw-barra-usado"
            :class="'gw-barra-usado--' + tonoUrgencia(v.ans_resolucion.estado)"
            :style="{ width: anchoConsumo(v.ans_resolucion) }"
          ></div>
        </div>
        <div class="gw-consumo-texto">
          <b>{{ tiempoLegible(v.ans_resolucion.minutos_imputables) }}</b>
          consumidos de un plazo de {{ tiempoLegible(v.ans_resolucion.presupuesto) }}
          <span v-if="v.ans_resolucion.minutos_excluidos > 0" class="gw-consumo-excluido">
            · {{ tiempoLegible(v.ans_resolucion.minutos_excluidos) }} excluidos del cómputo
          </span>
        </div>
      </div>

      <!-- Qué explica el tiempo. No se cuantifica el exceso sobre el plazo:
           hasta que la medición esté acordada con operación, un numero asi
           puede leerse como un reconocimiento de incumplimiento. El reparto
           del tiempo si se muestra, porque es un hecho del proceso. -->
      <div v-if="v.explicacion" class="gw-explicacion">
        <div class="gw-explicacion-titulo">Dónde estuvo el tiempo</div>
        <div class="gw-explicacion-motivo">{{ v.explicacion.motivo_principal }}</div>
        <ul class="gw-reparto" v-if="v.explicacion.reparto_del_tiempo.length">
          <li v-for="(r, j) in v.explicacion.reparto_del_tiempo" :key="j">
            {{ etiquetaResponsable(r.responsable) }}: {{ tiempoLegible(r.minutos) }}
          </li>
        </ul>
      </div>

      <!-- La línea de tiempo -->
      <ol v-if="v.linea_de_tiempo && v.linea_de_tiempo.length" class="gw-linea">
        <li v-for="(e, k) in v.linea_de_tiempo" :key="k" class="gw-hito">
          <span class="gw-hito-punto"></span>
          <div class="gw-hito-cuerpo">
            <div class="gw-hito-que">
              {{ e.etiqueta || e.que_paso }}
              <span v-if="e.responsable" class="gw-hito-quien">
                → queda en {{ etiquetaResponsable(e.responsable) }}
              </span>
            </div>
            <div class="gw-hito-pie">
              {{ fechaLarga(e.cuando) }}
              <span v-if="e.motivo" class="gw-hito-motivo">· {{ e.motivo }}</span>
            </div>
          </div>
        </li>
      </ol>
      <p v-else class="gw-sin-hitos">
        No hay eventos registrados para esta validación.
      </p>
    </div>
  </vx-card>
</template>

<script>
import { mapGetters } from "vuex";
import api from "../../mixins/api";
import slaFormat from "../../mixins/slaFormat";

export default {
  name: "ProjectSlaTimeline",
  mixins: [api, slaFormat],
  props: {
    projectId: { type: [Number, String], default: null },
  },

  data() {
    return {
      detalle: null,
      tituloAproximado:
        "Datos reconstruidos de un periodo anterior al inicio de la medición. " +
        "Sus tiempos son aproximados.",
    };
  },

  computed: {
    ...mapGetters(["userInfo"]),

    verLineaDeTiempo() {
      return !!(this.userInfo && this.userInfo.sla_timeline_access);
    },

    hayDatos() {
      return !!(this.detalle && this.validaciones.length);
    },
    validaciones() {
      return this.detalle ? this.detalle.validaciones || [] : [];
    },
  },

  methods: {
    anchoConsumo(ans) {
      if (!ans.presupuesto) return "0%";
      const pct = (ans.minutos_imputables / ans.presupuesto) * 100;
      return Math.max(2, Math.min(100, Math.round(pct))) + "%";
    },

    fechaCorta(iso) {
      if (!iso) return "";
      const d = this.aFecha(iso);
      if (!d) return "";
      return ("0" + d.getDate()).slice(-2) + "/" + ("0" + (d.getMonth() + 1)).slice(-2);
    },

    fechaLarga(iso) {
      const d = this.aFecha(iso);
      if (!d) return "";
      const hh = ("0" + d.getHours()).slice(-2);
      const mm = ("0" + d.getMinutes()).slice(-2);
      return this.fechaLegible(
        d.getFullYear() +
          "-" +
          ("0" + (d.getMonth() + 1)).slice(-2) +
          "-" +
          ("0" + d.getDate()).slice(-2)
      ) + ", " + hh + ":" + mm;
    },

    // El backend serializa como "2026-10-12 05:54:01 UTC", que Safari no
    // parsea con new Date(). Se normaliza a ISO antes.
    aFecha(valor) {
      if (!valor) return null;
      const normalizado = String(valor).replace(" UTC", "Z").replace(" ", "T");
      const d = new Date(normalizado);
      return isNaN(d.getTime()) ? null : d;
    },

    async initializeData() {
      // Detras del interruptor por cliente. Si esta apagado ni se pregunta:
      // el endpoint responderia 403 y ensuciaria la consola en cada proceso
      // que el cliente abra.
      if (this.projectId == null || !this.verLineaDeTiempo) return;
      const base = this.url().replace(/\/+$/, "");

      try {
        const request = await this.requestGetWithoutContent(
          base + "/api/v1/sla/projects/" + this.projectId,
          {}
        );
        if (request && request.status === 200 && request.data) {
          this.detalle = request.data;
        }
      } catch (e) {
        // 404 es el caso normal para los procesos anteriores a la medición.
        // Cualquier otro fallo tampoco debe tumbar el resto de la página.
        this.detalle = null;
      }
    },
  },

  watch: {
    projectId() {
      this.initializeData();
    },
  },

  mounted() {
    this.initializeData();
  },
};
</script>

<style scoped>
.gw-bajada {
  font-size: 12px;
  color: #9a9a9a;
  margin: -6px 0 20px;
  max-width: 72ch;
}

.gw-validacion {
  padding: 18px 0 20px;
  border-top: 1px solid #f0f0f0;
}
.gw-validacion:first-of-type {
  border-top: none;
  padding-top: 0;
}

.gw-v-cabecera {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.gw-v-identidad {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}
.gw-v-nombre {
  font-size: 15px;
  font-weight: 700;
  color: #3b3b3b;
}
.gw-v-estado {
  font-size: 12px;
  color: #6b6b6b;
}
.gw-v-aprox {
  font-size: 11px;
  color: #9a6d12;
  background: #fdf7e3;
  border-radius: 9px;
  padding: 1px 8px;
  cursor: help;
}
.gw-v-ans {
  display: flex;
  align-items: center;
  gap: 10px;
}
.gw-v-vence {
  font-size: 11px;
  color: #9a9a9a;
}

.gw-chip {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 11px;
  white-space: nowrap;
}
.gw-chip--neutro { background: #f2f2f2; color: #8a8a8a; }
.gw-chip--ok { background: #eaf5ef; color: #2e7d52; }
.gw-chip--aviso { background: #fdf4e3; color: #9a6d12; }
.gw-chip--alerta { background: #f6eceb; color: #a0503f; }

.gw-consumo {
  margin-top: 12px;
}
.gw-barra {
  height: 6px;
  border-radius: 3px;
  background: #f0f0f0;
  overflow: hidden;
}
.gw-barra-usado {
  height: 100%;
  border-radius: 3px;
  background: #5f0091;
}
.gw-barra-usado--ok { background: #2e7d52; }
.gw-barra-usado--aviso { background: #d99a1f; }
/* Mismo tono apagado que el chip: la barra llena ya dice que se paso del
   plazo, no hace falta que lo grite. */
.gw-barra-usado--alerta { background: #b06151; }
.gw-consumo-texto {
  font-size: 12px;
  color: #6b6b6b;
  margin-top: 6px;
}
.gw-consumo-texto b { color: #3b3b3b; }
.gw-consumo-excluido { color: #9a9a9a; }

.gw-explicacion {
  margin-top: 14px;
  padding: 12px 14px;
  background: #fafafa;
  border: 1px solid #efefef;
  border-radius: 9px;
}
.gw-explicacion-titulo {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .05em;
  text-transform: uppercase;
  color: #9a9a9a;
  margin-bottom: 5px;
}
.gw-explicacion-motivo {
  font-size: 13px;
  color: #3b3b3b;
}
.gw-reparto {
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 12px;
  color: #6b6b6b;
}

.gw-linea {
  list-style: none;
  margin: 18px 0 0;
  padding: 0 0 0 6px;
  border-left: 2px solid #efe7f3;
}
.gw-hito {
  position: relative;
  padding: 0 0 16px 20px;
}
.gw-hito:last-child {
  padding-bottom: 0;
}
.gw-hito-punto {
  position: absolute;
  left: -7px;
  top: 4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #5f0091;
  border: 2px solid #fff;
}
.gw-hito-que {
  font-size: 13px;
  color: #3b3b3b;
  font-weight: 600;
}
.gw-hito-quien {
  font-weight: 400;
  color: #6b6b6b;
}
.gw-hito-pie {
  font-size: 11.5px;
  color: #9a9a9a;
  margin-top: 2px;
}
.gw-hito-motivo {
  color: #6b6b6b;
}
.gw-sin-hitos {
  font-size: 12px;
  color: #9a9a9a;
  margin: 14px 0 0;
}
</style>
